import { DefaultFormFields, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { makeAutoObservable, ObservableMap, runInAction } from 'mobx';
import ModalDelegate from 'store/general/modalDelegate';
import { Primitive } from 'types/general';
import uniqid from 'uniqid';

export interface ReplicatedInfoItem {
  [key: string]: Primitive;
}

export type ReplicatedInfoItemWithId<ItemType extends ReplicatedInfoItem> = ItemType & {
  id: string;
};

interface ReplicatedInfoProps<ItemType extends ReplicatedInfoItem, ItemFormFieldsType extends DefaultFormFields> {
  itemFormFields: ItemFormFieldsType;
  syncItemsValue: (items: ItemType[]) => void;
  maxItems: number;
}

export class ReplicatedInfoDelegate<ItemType extends ReplicatedInfoItem, ItemFormFields extends DefaultFormFields> {
  private readonly _items = new ObservableMap<string, ItemType>();
  private readonly _addItemForm: FormController<ItemFormFields>;
  private readonly _editItemForm: FormController<ItemFormFields>;
  private _activeItemId: string | null = null;
  private readonly _syncItemsValue: (items: ItemType[]) => void;
  private readonly _maxItems: number;

  public readonly addItemModal = new ModalDelegate();
  public readonly editItemModal = new ModalDelegate();

  private _getItemsForSync(): ItemType[] {
    return Array.from(this._items.entries()).map(([id, item]) => ({ ...item, id } as ItemType));
  }

  private _addItem(): void {
    if (this._items.size >= this._maxItems) {
      return;
    }
    const id = uniqid();
    this._items.set(id, { ...this._addItemForm.getFormDataObject(), id } as unknown as ItemType);
    this._syncItemsValue(this._getItemsForSync());
  }

  private _updateItem(): void {
    if (!this._activeItemId) {
      throw new Error('Active item ID is not set');
    }
    this._items.set(this._activeItemId, {
      ...this._editItemForm.getFormDataObject(),
      id: this._activeItemId,
    } as unknown as ItemType);
    this._syncItemsValue(this._getItemsForSync());
  }

  public get items(): ObservableMap<string, ItemType> {
    return this._items;
  }

  public get itemsList(): ReplicatedInfoItemWithId<ItemType>[] {
    return Array.from(this._items.entries()).map(([id, item]) => ({ id, ...item }));
  }

  public get addItemForm(): FormController<ItemFormFields> {
    return this._addItemForm;
  }

  public get editItemForm(): FormController<ItemFormFields> {
    return this._editItemForm;
  }

  public get maxItems(): number {
    return this._maxItems;
  }

  public setActiveItemId(id: string | null): void {
    this._activeItemId = id;
  }

  public openForEdit(itemId: string, item: ItemType): void {
    this.setActiveItemId(itemId);
    this._editItemForm.populateFields(item);
    this.editItemModal.open();
  }

  public closeAddItemModal(): void {
    this._addItemForm.resetForm();
    this.addItemModal.close();
  }

  public closeEditItemModal(): void {
    this._editItemForm.resetForm();
    this.editItemModal.close();
    this.setActiveItemId(null);
  }

  public deleteItem(id: string): void {
    this._items.delete(id);
    this._syncItemsValue(this._getItemsForSync());
  }

  public populateItems(items: ItemType[]): void {
    this._items.clear();
    items.forEach((item) => {
      const id = item.id && typeof item.id === 'string' ? item.id : uniqid();
      this._items.set(id, { ...item, id } as ItemType);
    });
  }

  constructor(props: ReplicatedInfoProps<ItemType, ItemFormFields>) {
    this._syncItemsValue = props.syncItemsValue;
    this._maxItems = props.maxItems;
    this._addItemForm = new FormController<ItemFormFields>(props.itemFormFields, {
      onSubmit: (): Promise<void> =>
        Promise.resolve(
          runInAction(() => {
            this._addItem();
            this.closeAddItemModal();
          }),
        ),
    });
    this._editItemForm = new FormController<ItemFormFields>(props.itemFormFields, {
      onSubmit: (): Promise<void> =>
        Promise.resolve(
          runInAction(() => {
            this._updateItem();
            this.closeEditItemModal();
          }),
        ),
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
